'use client'

import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import { ClientNameAndMatterDescription } from '@repo/ui/ClientNameAndMatterDescription'
import { Avatar } from '@repo/ui/Avatar'
import MUITypography from '@mui/material/Typography'
import MUIStack from '@mui/material/Stack'
import { LockIcon } from '@repo/ui/LockIcon'

export const MatterSearchResultCard = ({
  matter,
  currentClientMatterNo = null,
  isListOption = true,
  disableArchived = false,
  disableGlobal = false,
  disableRestricted = false
}) => {
  const theme = useTheme()

  if (!matter) return <div data-testid="MatterSearchResultCard" />
  const {
    clientNo,
    matterNo,
    clientMatterNo,
    matterDescription,
    forename,
    surname,
    clientForename,
    clientSurname,
    clientName,
    feeEarnerId,
    feeEarnerDescription,
    dateArchived,
    workflowTypeId,
    isDisabled,
    isUserRestricted
  } = matter

  const avatarSize = isListOption ? '36px' : '24px'

  if (clientMatterNo === '-0') {
    // Blank matter option
    return (
      <MUIStack direction="row" alignItems="center" data-testid="MatterSearchResultCard">
        <Avatar sx={{ width: avatarSize, height: avatarSize, fontSize: '0.675rem', mr: 3 }} />
        <MUITypography sx={{ mb: 0, pointerEvents: 'none' }}>Blank matter</MUITypography>
      </MUIStack>
    )
  }

  const isCurrentMatter = currentClientMatterNo === clientMatterNo
  let width = '100%'
  if (!isListOption) width = '100%'
  const forenameDisplay = forename || clientForename
  const surnameDisplay = surname || clientSurname || clientName

  const isLocked = disableRestricted && isUserRestricted

  const decorator = getMatterDecoration({
    disableArchived,
    dateArchived,
    disableGlobal,
    workflowTypeId,
    isCurrentMatter,
    isListOption
  })

  return (
    <MUIStack
      direction="row"
      alignItems="center"
      data-testid="MatterSearchResultCard"
      sx={{
        width,
        padding: isListOption ? '5px 0px' : '0px 10px 0px 0px',
        position: 'relative',
        opacity: isDisabled ? 0.6 : 1,
        pointerEvents: isDisabled ? 'none' : 'all'
      }}
    >
      {feeEarnerDescription ? (
        <Avatar
          sx={{ width: avatarSize, height: avatarSize, fontSize: '0.675rem', mr: 2 }}
          feeEarnerId={feeEarnerId}
          name={feeEarnerDescription}
        />
      ) : null}

      {isListOption ? (
        <ClientNameAndMatterDescription
          clientNo={clientNo}
          matterNo={matterNo}
          forename={forenameDisplay}
          surname={surnameDisplay}
          matterDescription={matterDescription}
          isCurrentMatter={!!decorator}
          showMatterPill={false}
          sx={{ pointerEvents: 'none' }}
        />
      ) : (
        <MUITypography sx={{ mb: 0, pointerEvents: 'none', flex: 1 }}>
          <strong>{clientMatterNo}</strong> | {matterDescription}
        </MUITypography>
      )}

      {isLocked ? <LockIcon sx={{ width: '18px', height: '18px', mr: 2 }} /> : null}

      {!isLocked && decorator ? (
        <MUITypography
          style={{
            textTransform: 'uppercase',
            padding: '2px 4px',
            borderRadius: '4px',
            backgroundColor: theme.palette.background.slate100,
            fontSize: '0.675rem',
            color: theme.palette.text.primary,
            margin: 0
          }}
          variant="caption"
        >
          {decorator}
        </MUITypography>
      ) : null}
    </MUIStack>
  )
}

const getMatterDecoration = ({
  disableArchived,
  dateArchived,
  disableGlobal,
  workflowTypeId,
  isCurrentMatter,
  isListOption
}) => {
  if (!isListOption) return null

  let label = ''
  if (disableArchived && dateArchived) label = 'Archived'
  else if (disableGlobal && workflowTypeId === 'GLOBAL') label = 'Unavailable'
  else if (isCurrentMatter) label = 'Current'
  return label
}

MatterSearchResultCard.propTypes = {
  matter: PropTypes.object,
  currentClientMatterNo: PropTypes.string,
  isListOption: PropTypes.bool,
  disableArchived: PropTypes.bool,
  disableGlobal: PropTypes.bool,
  disableRestricted: PropTypes.bool
}
